/** Inline phone-confirmed session control proposal card. */
import { useState } from 'react';

import {
  SESSION_COMMAND_STATE_LABELS,
  type SessionCommandSendResult,
} from '../lib/session-control';
import type { SessionControlProposal } from '../lib/session-control-proposal';
import { useApp } from '../state/AppState';

export function SessionControlProposalCard({ proposal }: { proposal: SessionControlProposal }) {
  const { sessions, sendSessionCommand, sessionCommands } = useApp();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [result, setResult] = useState<SessionCommandSendResult | null>(null);

  const session = sessions.find(
    (item) => item.source === proposal.source && item.session_id === proposal.session_id,
  );
  const expired = proposal.expires_at * 1000 <= Date.now();
  const actionAvailable =
    session?.control?.enabled === true && session.control.capabilities.includes(proposal.command);
  const commandId = result?.command_id;
  const ledger = commandId === undefined ? undefined : sessionCommands[commandId];
  const commandLabel = proposal.command === 'interrupt' ? '中断当前 turn' : '发送一条消息';

  if (dismissed) return null;

  const confirm = async (): Promise<void> => {
    if (session === undefined || busy || expired) return;
    setBusy(true);
    try {
      const sent = await sendSessionCommand({
        session,
        action: proposal.command,
        text: proposal.command === 'message' ? (proposal.text ?? '') : '',
      });
      setResult(sent);
    } finally {
      setBusy(false);
    }
  };

  const status = (() => {
    if (expired) return '提案已过期，请让光耀重新生成';
    if (session === undefined) return '目标会话不在当前列表，请刷新会话列表';
    if (!actionAvailable) return '当前会话未声明该控制能力，无法从手机执行';
    if (result === null) return null;
    if (!result.ok) return result.error ?? '发送失败';
    if (ledger !== undefined) return `状态：${SESSION_COMMAND_STATE_LABELS[ledger.state]}`;
    return '已发送（等待执行结果）';
  })();

  return (
    <div className="mt-1.5 rounded-2xl border border-indigo-400/30 bg-indigo-500/[0.07] p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-indigo-100">会话控制提案</span>
        <span className="rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[10px] text-slate-300">
          {session?.title ?? proposal.session_id.slice(0, 18)}
        </span>
      </div>
      <p className="mt-1.5 text-slate-300">
        目标：{proposal.source.toUpperCase()} · {proposal.session_id.slice(0, 18)}
      </p>
      <p className="mt-0.5 text-slate-300">动作：{commandLabel}</p>
      {proposal.command === 'message' && proposal.text !== undefined ? (
        <p className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-black/25 px-2 py-1.5 text-slate-200">
          {proposal.text}
        </p>
      ) : null}
      {status !== null ? <p className="mt-1.5 text-[11px] text-slate-400">{status}</p> : null}
      {!expired && session !== undefined && actionAvailable && result === null ? (
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={busy}
            className="rounded-lg bg-indigo-500 px-3 py-1.5 text-[11px] font-medium text-white disabled:opacity-50"
          >
            {busy ? '签名中…' : '确认发送'}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={busy}
            className="rounded-lg border border-white/10 px-3 py-1.5 text-[11px] text-slate-300 disabled:opacity-50"
          >
            取消
          </button>
        </div>
      ) : null}
    </div>
  );
}
